import { IWebcamItem } from 'api/region/types';
import {
  fetchResortAccommodations,
  fetchResortCameras,
  fetchResortFeaturedCam, fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortShortInfo, fetchResortShortWeatherReport,
} from 'api/resort/static';
import { IRelatedNews, IResortPageInfo, IResortPathInfo } from 'api/resort/types';
import { AxiosResponse } from 'axios';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { FeaturedCamera } from 'components/common/FeaturedCamera';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { LdJsonBreadCrumbsResort } from 'components/LdJson';
import { CameraList } from 'components/resort/CameraList';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { RegularAd } from 'googlead/components/RegularAd';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Routes } from 'routes';

import { getResortSlug } from 'util/memcache';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { useIsMobile } from 'util/useIsMobile';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import styles from './cams.module.scss';

export type ResortCamerasPageProps = {
  resort: IResortPageInfo,
  cameras: Array<IWebcamItem> | null,
  featured: IWebcamItem | null,
  pathInfo: IResortPathInfo,
  relatedNews: IRelatedNews | null,
  accommodations: IAccommodationPage | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-cams-page';
export const TEMPLATE_ID = '2.2.3';
export const TEMPLATE_NAME = 'Resort Cams';

export default function ResortCamerasPage({
  resort,
  cameras,
  featured,
  pathInfo,
  relatedNews,
}: ResortCamerasPageProps) {
  const [curCam, setCurCam] = useState<IWebcamItem>();

  useEffect(() => {
    if (!curCam && featured) {
      setCurCam(featured);
    }
  }, [curCam, featured]);

  const refScroll = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const changeFeaturedCam = useCallback((camera: IWebcamItem) => {
    setCurCam(camera);
    if (refScroll.current && !isMobile) {
      window.scrollTo(0, refScroll.current.offsetTop - 80);
    }
  }, [curCam, refScroll]);

  const { t } = useOTSTranslation('common');
  const camerasWithResort = cameras?.map((cam) => ({ ...cam, resort: pathInfo }));

  if (!resort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.cams.seoTitle', { resort: resort.title })} />
          <BlockContent>
            {t('resort.cams.seoText', { resort: resort.title })}
          </BlockContent>
        </BoxWithShadow>
        {curCam && (
          <FeaturedCamera
            isFresh
            cam={curCam}
            refScroll={refScroll}
            className={styles.hideOnMobile}
            isResortCams
          />
        )}
        {cameras && curCam && (
          <CameraList
            skipSingleCamera
            showSnowReport={false}
            cameras={camerasWithResort ?? []}
            isFresh
            onCamSelect={changeFeaturedCam}
            currentId={curCam?.uuid}
            startAllLiveOnMobile
            isResortCams
            showAsSlider
          />
        )}
        <RegularAd isFirst />
        <AiAssistantWidget
          templateId={TEMPLATE_ID}
          name={resort.title}
          margin={{ marginTop: '0rem', marginBottom: '2.5rem' }}
        />
        <PlanYourTrip
          resort={pathInfo}
          hasSkiRental={pathInfo.hasSkiRental}
          hasFlights={pathInfo.hasFlights}
          hasCarRental={pathInfo.hasCarRental}
          liftsUrl={resort.liftsUrl}
          rentalUrl={resort.rentalUrl}
          lessonsUrl={resort.lessonsUrl}
          isPartner={resort.isPartner}
          resortCountry={resort.region?.country_code}
        />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={resort.region}
        fn={Routes.resortCams}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortCamerasPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const relatedNews = await fetchResortRelatedNews(uuid);

  return {
    type: 'resort',
    route: 'cams',
    pathInfo: cache.data,
    relatedNews,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    resort: await fetchResortShortInfo(uuid),
    cameras: await fetchResortCameras(uuid),
    featured: await fetchResortFeaturedCam(uuid),

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.CAMS,
      tab: CurrentTab.SNOW,
      head: HeaderNavCurLink.SNOW_REPORTS,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort cams',
    hero: {
      title: 'resort.cams.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.cams.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.cams.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),

  };
};

// This function gets called at build time
export const getResortCamerasPageStaticProps = wrapStaticProps<ResortCamerasPageProps, { region: string, resort: string }>(fetchProps);
export const getResortCamerasPageServerSideProps = wrapServerSideProps<ResortCamerasPageProps, { region: string, resort: string }>(fetchProps);
